import React, { useMemo, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label"
            placeholder={placeholder}
            className={className || "form-input"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%', display: 'flex', alignItems: 'center' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
        />
    );
};

const EducationHistoryForm = ({ isOpen, onClose, onSubmit, formData, setFormData, educationLevels = [], countries = [], isEditing }) => {
    const countryOptions = useMemo(() => {
        return countries.map(c => ({
            label: c.name,
            value: c.name
        }));
    }, [countries]);

    const modeOptions = [
        { label: 'Đầy đủ', value: 'date' },
        { label: 'Tháng/Năm', value: 'month' },
        { label: 'Chỉ Năm', value: 'year' }
    ];

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDateChange = (name, dateObj) => {
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString } });
    };

    const getDateFormat = (accuracy) => {
        if (accuracy === 'year') return 'yy';
        if (accuracy === 'month') return 'mm/yy';
        return 'dd/mm/yy';
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box form-modal-box" style={{ maxHeight: '95vh', overflowY: 'auto' }}>
                <div className="modal-header">
                    <h3>{isEditing ? "Cập nhật Quá trình đào tạo" : "Thêm mới Quá trình đào tạo"}</h3>
                    <button
                        type="button"
                        className="close-btn"
                        onClick={onClose}
                        style={{ fontSize: '24px', lineHeight: '1', display: 'flex', alignItems: 'center' }}
                    >
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="educationForm" onSubmit={onSubmit}>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Trình độ *</label>
                            <select name="EducationLevel" className="form-input" value={formData.EducationLevel || ''} onChange={handleChange} required>
                                <option value="">Chọn trình độ</option>
                                {educationLevels.map((lvl) => (
                                    <option key={lvl.Id} value={lvl.Name}>{lvl.Name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tại trường *</label>
                            <input type="text" name="School" className="form-input" value={formData.School || ''} onChange={handleChange} required />
                        </div>

                        <div className="form-grid-2">
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngành *</label>
                                <input type="text" name="Major" className="form-input" value={formData.Major || ''} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Chuyên ngành *</label>
                                <input type="text" name="Specialization" className="form-input" value={formData.Specialization || ''} onChange={handleChange} required />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Quốc gia *</label>
                            <SearchableFocusDropdown
                                value={formData.Country || ''}
                                options={countryOptions}
                                onChange={(e) => handleChange({ target: { name: 'Country', value: e.value } })}
                                placeholder="Chọn quốc gia"
                                panelUniqueClass="dropdown-panel-override-education"
                            />
                        </div>

                        <div className="form-grid-2" style={{ alignItems: 'start' }}>
                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ fontWeight: '500', margin: 0 }}>Từ ngày *</label>
                                    <SelectButton
                                        value={formData.StartDateAccuracy || 'date'}
                                        options={modeOptions}
                                        onChange={(e) => e.value && setFormData({ ...formData, StartDateAccuracy: e.value })}
                                        className="custom-date-toggle"
                                    />
                                </div>
                                <Calendar
                                    id="StartDate"
                                    name="StartDate"
                                    view={formData.StartDateAccuracy || 'date'}
                                    value={(formData.StartDate && !isNaN(new Date(formData.StartDate).getTime())) ? new Date(formData.StartDate) : null}
                                    dateFormat={getDateFormat(formData.StartDateAccuracy)}
                                    showIcon
                                    onChange={(e) => handleDateChange('StartDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ fontWeight: '500', margin: 0 }}>Đến ngày</label>
                                    <SelectButton
                                        value={formData.EndDateAccuracy || 'date'}
                                        options={modeOptions}
                                        onChange={(e) => e.value && setFormData({ ...formData, EndDateAccuracy: e.value })}
                                        className="custom-date-toggle"
                                    />
                                </div>
                                <Calendar
                                    id="EndDate"
                                    name="EndDate"
                                    view={formData.EndDateAccuracy || 'date'}
                                    value={(formData.EndDate && !isNaN(new Date(formData.EndDate).getTime())) ? new Date(formData.EndDate) : null}
                                    dateFormat={getDateFormat(formData.EndDateAccuracy)}
                                    showIcon
                                    onChange={(e) => handleDateChange('EndDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                />
                            </div>
                        </div>

                    </form>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    <button type="button" className="btn-cancel" onClick={onClose}>Hủy</button>
                    <button type="submit" form="educationForm" className="btn-submit">Hoàn tất</button>
                </div>
            </div>
        </div>
    );
};

export default EducationHistoryForm;